/**
 * @fileoverview Project Modal Component
 *
 * Multi-mode modal for creating new project records.
 * Supports Quick Add and Full Add modes (no edit mode —
 * projects are edited inline on AdminProjectDetail).
 *
 * Quick Add: Project Name, Project Address, Project Type, Client (dropdown)
 * Full Add: + Budget, Start Date, Est. Completion, Project Manager, Notes
 *
 * Project Manager dropdown is populated from the Team table
 * using the same query pattern as ProjectInfoHeader.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../../utils/dateUtils
 * @requires ./AdminFormModal.css
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaBuilding,
  FaTimes,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaSearch,
  FaUserTie,
  FaPlusCircle,
} from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import "./AdminFormModal.css";

// =============================================================================
// CONSTANTS
// =============================================================================

/** Available project types matching Airtable Single Select options */
const PROJECT_TYPES = [
  "Custom Home",
  "Spec House",
  "Commercial",
  "Pool Project",
  "Internal",
];

/** Default empty form state */
const EMPTY_FORM = {
  projectName: "",
  projectAddress: "",
  projectType: "",
  clientId: "",
  budget: "",
  startDate: "",
  estimatedCompletion: "",
  projectManager: [],
  notes: "",
};

// =============================================================================
// CLIENT SEARCH SUB-COMPONENT
// =============================================================================

/**
 * Searchable client dropdown
 * @param {Object} props - Component props
 * @param {Array} props.clients - Available clients [{id, clientName, email}]
 * @param {string} props.value - Selected client ID
 * @param {Function} props.onChange - Change handler (receives client ID)
 * @param {boolean} props.isLoading - Whether clients are loading
 */
const ClientSearchDropdown = ({ clients, value, onChange, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  /** Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Get the selected client's display name */
  const selectedClient = clients.find((c) => c.id === value);

  /** Filter clients by search term */
  const filteredClients = clients.filter(
    (c) =>
      c.clientName?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()),
  );

  /**
   * Handle selecting a client
   * @param {string} clientId - Selected client record ID
   */
  const handleSelect = (clientId) => {
    onChange(clientId);
    setIsOpen(false);
    setSearch("");
  };

  /**
   * Clear the selected client
   * @param {Event} e - Click event
   */
  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  return (
    <div className="afm-multiselect-wrapper" ref={wrapperRef}>
      <div
        className={`afm-multiselect-trigger ${isOpen ? "afm-multiselect-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedClient ? (
          <span className="afm-multiselect-tag">
            {selectedClient.clientName}
            <span className="afm-multiselect-tag-remove" onClick={handleClear}>
              <FaTimes />
            </span>
          </span>
        ) : (
          <span className="afm-multiselect-placeholder">
            {isLoading
              ? "Loading clients..."
              : "Select unattached client (optional)"}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="afm-multiselect-dropdown">
          {/* Search input */}
          <div className="afm-multiselect-search">
            <div className="afm-multiselect-search-bar">
              <FaSearch className="afm-multiselect-search-icon" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                onClick={(e) => e.stopPropagation()}
                autoFocus
                className="afm-multiselect-search-input"
              />
            </div>
          </div>

          {/* No client option */}
          <div
            className={`afm-multiselect-option afm-multiselect-option-italic ${!value ? "afm-multiselect-selected" : ""}`}
            onClick={() => handleSelect("")}
          >
            No client (assign later)
          </div>

          {/* Client list */}
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className={`afm-multiselect-option ${
                value === client.id ? "afm-multiselect-selected" : ""
              }`}
              onClick={() => handleSelect(client.id)}
            >
              <span>
                {client.clientName}
                {client.email && (
                  <span className="afm-multiselect-option-meta">
                    {client.email}
                  </span>
                )}
              </span>
            </div>
          ))}

          {filteredClients.length === 0 && search && (
            <div className="afm-multiselect-empty">No clients found</div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PROJECT MODAL COMPONENT
// =============================================================================

/**
 * ProjectModal Component
 *
 * Fetches its own data (unattached clients, project managers)
 * internally when the modal opens.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {"quick"|"full"} [props.mode="quick"] - Initial mode
 * @param {Function} [props.onSave] - Callback after successful save
 * @param {Object} props.mutations - { createProject } mutation object
 */
const ProjectModal = ({
  isOpen,
  onClose,
  mode: initialMode = "quick",
  onSave,
  mutations,
}) => {
  // ===================== DATA FETCHING =====================

  /** Fetch unattached clients — same endpoint as AttachClientModal */
  const { data: unattachedClients, isLoading: clientsLoading } = useQuery({
    queryKey: ["unattachedClients"],
    queryFn: async () => {
      const getUnattachedClients = httpsCallable(
        functions,
        "getUnattachedClients",
      );
      const result = await getUnattachedClients();
      const raw = result.data.data.clients || [];
      return raw.map((c) => ({
        id: c.id,
        clientName: c.name || "",
        email: c.email || "",
      }));
    },
    enabled: isOpen,
    staleTime: 2 * 60 * 1000,
  });

  /** Fetch project managers from Team table */
  const { data: projectManagersList, isLoading: pmLoading } = useQuery({
    queryKey: ["projectManagers"],
    queryFn: async () => {
      const getProjectManagersFn = httpsCallable(
        functions,
        "getProjectManagers",
      );
      const result = await getProjectManagersFn();
      return result.data?.data || [];
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const clients = unattachedClients || [];
  const projectManagers = projectManagersList || [];
  // ===================== STATE =====================

  const [currentMode, setCurrentMode] = useState(initialMode);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  /** Baseline form snapshot for detecting unsaved changes */
  const baselineRef = useRef({ ...EMPTY_FORM });

  // ===================== EFFECTS =====================

  /** Reset form when modal opens */
  useEffect(() => {
    if (!isOpen) return;

    setCurrentMode(initialMode);
    setForm({ ...EMPTY_FORM });
    baselineRef.current = { ...EMPTY_FORM };
    setErrors({});
    setFormError("");
    setIsSuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ===================== HANDLERS =====================

  /**
   * Update a single form field
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormError("");
  }, []);

  /**
   * Toggle a project manager in the selection array
   * @param {string} managerId - Team record ID
   */
  const toggleProjectManager = (managerId) => {
    setForm((prev) => {
      const current = prev.projectManager;
      if (current.includes(managerId)) {
        return {
          ...prev,
          projectManager: current.filter((id) => id !== managerId),
        };
      } else {
        return { ...prev, projectManager: [...current, managerId] };
      }
    });
  };

  /**
   * Validate the form based on current mode
   * Quick Add requires: Project Name, Project Address, Project Type
   * Client is optional in all modes.
   * @return {boolean} True if valid
   */
  const validate = () => {
    const newErrors = {};

    if (!form.projectName.trim()) {
      newErrors.projectName = "Project name is required";
    }

    if (!form.projectAddress.trim()) {
      newErrors.projectAddress = "Project address is required";
    }

    if (!form.projectType) {
      newErrors.projectType = "Project type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submit the form — create new project
   */
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setFormError("");

    try {
      const payload = { ...form };

      // Convert budget string to number
      if (payload.budget) {
        payload.budget = Number(payload.budget.replace(/[^0-9.]/g, ""));
      } else {
        delete payload.budget;
      }

      // Remove empty fields
      Object.keys(payload).forEach((key) => {
        const val = payload[key];
        if (val === "" || val === null || val === undefined) {
          delete payload[key];
        }
        if (Array.isArray(val) && val.length === 0) {
          delete payload[key];
        }
      });

      await mutations.createProject.mutateAsync(payload);

      setIsSuccess(true);

      // Auto-close after brief success display
      setTimeout(() => {
        setIsSuccess(false);
        onSave?.();
        onClose();
      }, 1200);
    } catch (error) {
      console.error("[ProjectModal] Save error:", error);
      setFormError(
        error?.message || "Failed to create project. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Close the modal, warning first if there are unsaved changes.
   */
  const handleClose = () => {
    const isDirty =
      JSON.stringify(form) !== JSON.stringify(baselineRef.current);
    if (isDirty && !isSuccess) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        return;
      }
    }
    onClose();
  };

  // ===================== RENDER GUARDS =====================

  if (!isOpen) return null;

  const showFullFields = currentMode === "full";
  const modalTitle =
    currentMode === "full" ? "Add Project — Full Details" : "Quick Add Project";

  // ===================== RENDER =====================

  return (
    <div className="afm-overlay" onClick={handleClose}>
      <div
        className={`afm-modal ${showFullFields ? "afm-modal-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="afm-header">
          <div className="afm-header-left">
            <div className="afm-header-icon">
              <FaBuilding />
            </div>
            <h2 className="afm-title">{modalTitle}</h2>
          </div>
          <button
            className="afm-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="afm-mode-toggle">
          <button
            className={`afm-mode-btn ${
              currentMode === "quick" ? "afm-mode-active" : ""
            }`}
            onClick={() => setCurrentMode("quick")}
          >
            Quick Add
          </button>
          <button
            className={`afm-mode-btn ${
              currentMode === "full" ? "afm-mode-active" : ""
            }`}
            onClick={() => setCurrentMode("full")}
          >
            Full Details
          </button>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="afm-body">
            <div className="afm-success">
              <div className="afm-success-icon">
                <FaCheck />
              </div>
              <h3 className="afm-success-title">Project Created!</h3>
              <p className="afm-success-message">
                {form.projectName} has been added.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Form Body */}
            <div className="afm-body">
              {/* Form Error Banner */}
              {formError && (
                <div className="afm-form-error">
                  <FaExclamationTriangle />
                  <span>{formError}</span>
                </div>
              )}

              {/* === SECTION: Project Info === */}
              <div className="afm-section">
                <div className="afm-section-title">Project Information</div>

                <div className="afm-field">
                  <label className="afm-label">
                    Project Name <span className="afm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`afm-input ${
                      errors.projectName ? "afm-input-error" : ""
                    }`}
                    value={form.projectName}
                    onChange={(e) =>
                      handleChange("projectName", e.target.value)
                    }
                    placeholder="e.g. Smith Residence"
                    autoFocus
                  />
                  {errors.projectName && (
                    <div className="afm-error">{errors.projectName}</div>
                  )}
                </div>

                <div className="afm-field">
                  <label className="afm-label">
                    Project Address <span className="afm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`afm-input ${
                      errors.projectAddress ? "afm-input-error" : ""
                    }`}
                    value={form.projectAddress}
                    onChange={(e) =>
                      handleChange("projectAddress", e.target.value)
                    }
                    placeholder="Full street address"
                  />
                  {errors.projectAddress && (
                    <div className="afm-error">{errors.projectAddress}</div>
                  )}
                </div>

                <div className="afm-row">
                  <div className="afm-field">
                    <label className="afm-label">
                      Project Type <span className="afm-required">*</span>
                    </label>
                    <select
                      className={`afm-select ${
                        errors.projectType ? "afm-input-error" : ""
                      }`}
                      value={form.projectType}
                      onChange={(e) =>
                        handleChange("projectType", e.target.value)
                      }
                    >
                      <option value="">Select type...</option>
                      {PROJECT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {errors.projectType && (
                      <div className="afm-error">{errors.projectType}</div>
                    )}
                  </div>
                  <div className="afm-field">
                    <label className="afm-label">Client</label>
                    <ClientSearchDropdown
                      clients={clients}
                      value={form.clientId}
                      onChange={(val) => handleChange("clientId", val)}
                      isLoading={clientsLoading}
                    />
                  </div>
                </div>
              </div>

              {/* === FULL-ONLY SECTIONS === */}
              {showFullFields && (
                <>
                  {/* Budget & Timeline */}
                  <div className="afm-section">
                    <div className="afm-section-title">Budget & Timeline</div>

                    <div className="afm-field">
                      <label className="afm-label">Budget</label>
                      <div className="afm-budget-wrapper">
                        <span className="afm-budget-prefix">$</span>
                        <input
                          type="text"
                          className="afm-input"
                          value={form.budget}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^0-9,]/g,
                              "",
                            );
                            handleChange("budget", value);
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="afm-row">
                      <div className="afm-field">
                        <label className="afm-label">Start Date</label>
                        <input
                          type="date"
                          className="afm-input"
                          value={form.startDate}
                          onChange={(e) =>
                            handleChange("startDate", e.target.value)
                          }
                        />
                      </div>
                      <div className="afm-field">
                        <label className="afm-label">Est. Completion</label>
                        <input
                          type="date"
                          className="afm-input"
                          value={form.estimatedCompletion}
                          onChange={(e) =>
                            handleChange("estimatedCompletion", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Project Manager */}
                  <div className="afm-section">
                    <div className="afm-section-title">
                      <FaUserTie className="afm-section-title-icon" />
                      Project Manager(s)
                    </div>

                    <div className="afm-pm-list">
                      {pmLoading ? (
                        <div className="afm-pm-empty">Loading managers...</div>
                      ) : projectManagers.length === 0 ? (
                        <div className="afm-pm-empty">
                          No project managers found
                        </div>
                      ) : (
                        projectManagers.map((pm) => (
                          <label key={pm.id} className="afm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={form.projectManager.includes(pm.id)}
                              onChange={() => toggleProjectManager(pm.id)}
                            />
                            <span>{pm.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="afm-section">
                    <div className="afm-section-title">Notes</div>
                    <div className="afm-field">
                      <textarea
                        className="afm-textarea"
                        value={form.notes}
                        onChange={(e) => handleChange("notes", e.target.value)}
                        placeholder="Internal project notes..."
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="afm-footer">
              <button
                className="afm-btn afm-btn-secondary"
                onClick={handleClose}
                type="button"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                className="afm-btn afm-btn-primary"
                onClick={handleSubmit}
                disabled={isSaving}
                type="button"
                aria-label="Save"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="spinning" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaPlusCircle /> Create Project
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectModal;
